import { useState } from "react";
import { Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { errorText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FormError } from "../login/Fields";
import { RecoveryCodes } from "../login/RecoveryCodes";
import { QrKey } from "../TwoFactorSetup";
import { SecondFactorForm } from "./SecondFactorForm";
import { StepDialog } from "./StepDialog";

const TEXT = {
  confirm: ["Move 2-step to a new phone", "First confirm it's you with your current phone, or a recovery code if it's gone."],
  scan: ["Scan with the new phone", "Your old phone keeps working until the new one's code is accepted."],
  codes: ["Your new recovery codes", null],
};

const ScanNewPhone = ({ setup, onMoved }) => {
  const { confirmMfaMove } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onMoved(await confirmMfaMove(code.trim()));
    } catch (err) {
      setError(errorText(err, "That code didn't match."));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="move-2fa-scan-form">
      <QrKey setup={setup} testid="move-2fa" />
      <div className="space-y-1.5">
        <Label htmlFor="move-2fa-code">The 6-digit code the NEW phone shows</Label>
        <Input id="move-2fa-code" data-testid="move-2fa-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required
          placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          className="h-12 rounded-full bg-white text-center font-mono2 text-xl tracking-[0.3em]" />
      </div>
      <FormError text={error} />
      <Button data-testid="move-2fa-confirm-btn" type="submit" disabled={busy || code.length !== 6} className="h-12 w-full rounded-full font-bold gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} Switch to this phone
      </Button>
    </form>
  );
};

// Account settings: confirm with the old factor -> scan a new QR -> the old app stops working -> new recovery codes.
export const MoveTwoStepDialog = ({ open, onOpenChange }) => {
  const { startMfaMove } = useAuth();
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState(null);
  const step = codes ? "codes" : setup ? "scan" : "confirm";
  const close = (next) => { if (!next) { setSetup(null); setCodes(null); } onOpenChange(next); };
  const moved = (data) => { setCodes(data.recovery_codes); toast.success("2-step sign-in moved to the new phone."); };

  return (
    <StepDialog open={open} onOpenChange={close} testid="move-2fa-dialog" title={TEXT[step][0]} description={TEXT[step][1]}
      hideTitle={step === "codes"}>
      {step === "confirm" && <SecondFactorForm onConfirm={async (body) => setSetup(await startMfaMove(body))} label="Continue" />}
      {step === "scan" && <ScanNewPhone setup={setup} onMoved={moved} />}
      {step === "codes" && <RecoveryCodes codes={codes} onContinue={() => close(false)} />}
    </StepDialog>
  );
};
